import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { startCashfreeCheckout } from "../lib/cashfree";
import ManualUPICheckout from "../components/ManualUPICheckout";
import { Button } from "../components/ui/button";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  CreditCard,
  DeviceMobile,
  Bank,
  Wallet,
  CheckCircle,
  LockKey,
} from "@phosphor-icons/react";

const UPI_APPS = [
  { name: "GPay", color: "bg-white" },
  { name: "PhonePe", color: "bg-white" },
  { name: "Paytm", color: "bg-white" },
  { name: "BHIM", color: "bg-white" },
];

export default function CheckoutPage() {
  const { bookingId } = useParams();
  const nav = useNavigate();
  const [booking, setBooking] = useState(null);
  const [order, setOrder] = useState(null);
  const [method, setMethod] = useState("upi");
  const [upiId, setUpiId] = useState("");
  const [processing, setProcessing] = useState(false);

  const [orderError, setOrderError] = useState(null);

  useEffect(() => {
    api.get(`/bookings/${bookingId}`).then(({ data }) => {
      setBooking(data);
      if (data.status === "confirmed") {
        toast.info("This booking is already paid");
      }
    });
    api.post(`/payments/create-order/booking/${bookingId}`)
      .then(({ data }) => setOrder(data))
      .catch((e) => {
        const msg = formatApiError(e.response?.data?.detail) || "Payment gateway unavailable";
        setOrderError(msg);
      });
  }, [bookingId]);

  const pay = async (preferUpi = false) => {
    if (!order) return;
    setProcessing(true);

    // ---------- Cashfree branch ----------
    if (order.provider === "cashfree") {
      await startCashfreeCheckout({
        kind: "booking",
        refId: bookingId,
        onFail: () => setProcessing(false),
      });
      return; // browser will redirect to Cashfree hosted checkout
    }

    // ---------- Razorpay branch ----------
    if (!window.Razorpay) {
      toast.error("Payment gateway not loaded. Please refresh and try again.");
      setProcessing(false);
      return;
    }

    const options = {
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      name: "Zyro",
      description: `Booking ${bookingId}`,
      order_id: order.order_id,
      prefill: { name: booking.user_name, email: booking.user_email },
      handler: async (res) => {
        try {
          await api.post("/payments/verify", {
            kind: "booking",
            ref_id: bookingId,
            razorpay_order_id: res.razorpay_order_id,
            razorpay_payment_id: res.razorpay_payment_id,
            razorpay_signature: res.razorpay_signature,
          });
          toast.success("Payment successful");
          nav("/bookings");
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || "Verification failed");
        } finally {
          setProcessing(false);
        }
      },
      theme: { color: "#111827" },
      modal: { ondismiss: () => setProcessing(false) },
    };

    // Prefer UPI: put UPI block first, then let all other methods show below
    if (preferUpi) {
      options.config = {
        display: {
          blocks: {
            upi: { name: "Pay using UPI (recommended)", instruments: [{ method: "upi" }] },
          },
          sequence: ["block.upi"],
          preferences: { show_default_blocks: true },
        },
      };
    }

    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  if (!booking)
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12" data-testid="checkout-page">
      <p className="text-xs tracking-[0.3em] uppercase text-gold mb-3">Almost there</p>
      <h1 className="font-serif text-4xl text-midnight mb-10">Complete your payment</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-4">
          {orderError ? (
            <div className="p-6 bg-red-50 border border-red-200 rounded-2xl" data-testid="payment-error">
              <p className="text-xs tracking-widest uppercase text-red-700 mb-2">⚠️ Payment gateway not ready</p>
              <p className="text-sm text-red-900">{orderError}</p>
              <p className="text-xs text-red-700 mt-3">Please try again in a few minutes, or contact conect@zyroapp.in.</p>
            </div>
          ) : order ? (
            order.provider === "manual_upi" ? (
              <ManualUPICheckout
                order={order}
                kind="booking"
                refId={bookingId}
                entityStatus={booking?.manual_upi_status || "awaiting_proof"}
                onSubmitted={() => api.get(`/bookings/${bookingId}`).then(({ data }) => setBooking(data))}
              />
            ) : (
            <>
              {/* UPI-first hero card */}
              <div className="p-6 bg-gradient-to-br from-emerald-50 via-white to-emerald-50 border border-emerald-200 rounded-2xl" data-testid="upi-hero">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <DeviceMobile size={22} weight="fill" className="text-emerald-700" />
                  </div>
                  <div>
                    <p className="text-[10px] tracking-widest uppercase text-emerald-700">Fastest · Zero fees</p>
                    <p className="font-serif text-xl text-midnight">Pay via UPI</p>
                  </div>
                </div>
                <p className="text-sm text-neutral-700 mb-4">
                  One-tap payment through your favourite UPI app. Money reaches Zyro in seconds; booking gets confirmed instantly.
                </p>
                <div className="flex flex-wrap gap-2 mb-5">
                  {UPI_APPS.map((a) => (
                    <span key={a.name} className="px-3 py-1.5 rounded-full bg-white border border-emerald-200 text-xs font-medium text-neutral-700 shadow-sm">
                      {a.name}
                    </span>
                  ))}
                  <span className="px-3 py-1.5 rounded-full bg-white border border-emerald-200 text-xs font-medium text-neutral-500">+ any UPI ID</span>
                </div>
                <Button
                  onClick={() => pay(true)}
                  disabled={processing || booking.status === "confirmed"}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-base"
                  data-testid="pay-upi-btn"
                >
                  {processing ? "Opening UPI..." : (
                    <span className="flex items-center gap-2">
                      <DeviceMobile size={18} weight="fill" /> Pay ₹{booking.total_amount.toLocaleString("en-IN")} via UPI
                    </span>
                  )}
                </Button>
                <p className="text-[10px] text-center text-emerald-800 mt-3 tracking-widest uppercase">🔒 Powered by Razorpay · 100% secure</p>
              </div>

              {/* Other methods hint */}
              <div className="p-6 bg-white border border-black/10 rounded-2xl" data-testid="other-methods">
                <p className="text-xs tracking-widest uppercase text-neutral-500 mb-2">Or use another method</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-stone text-xs"><CreditCard size={12} /> Cards</span>
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-stone text-xs"><Bank size={12} /> Netbanking</span>
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-stone text-xs"><Wallet size={12} /> Wallets</span>
                </div>
                <Button
                  onClick={() => pay(false)}
                  variant="outline"
                  disabled={processing || booking.status === "confirmed"}
                  className="w-full border-midnight text-midnight hover:bg-stone"
                  data-testid="pay-other-btn"
                >
                  <LockKey size={14} className="mr-2" /> Choose another method
                </Button>
              </div>
            </>
            )
          ) : (
            <div className="p-6 bg-white border border-black/10 rounded-2xl flex items-center gap-3" data-testid="payment-loading">
              <div className="w-5 h-5 border-2 border-gold border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-neutral-600">Preparing secure payment…</p>
            </div>
          )}
        </div>

        <aside>
          <div className="bg-white border border-black/10 rounded-2xl p-6 sticky top-28" data-testid="checkout-summary">
            <h3 className="font-serif text-xl mb-4 text-midnight">Order Summary</h3>
            {booking.hotel_image && (
              <img src={booking.hotel_image} alt="" className="w-full h-32 object-cover rounded-xl mb-4" />
            )}
            <p className="font-medium text-midnight mb-1">{booking.hotel_name}</p>
            <p className="text-xs text-neutral-500 mb-4">{booking.hotel_city}</p>

            <div className="space-y-2 text-sm border-y border-black/10 py-4 mb-4">
              <div className="flex justify-between text-neutral-600">
                <span>Check-in</span><span>{new Date(booking.check_in).toLocaleDateString("en-IN")}</span>
              </div>
              <div className="flex justify-between text-neutral-600">
                <span>Check-out</span><span>{new Date(booking.check_out).toLocaleDateString("en-IN")}</span>
              </div>
              <div className="flex justify-between text-neutral-600">
                <span>Nights × Rooms</span><span>{booking.nights} × {booking.rooms}</span>
              </div>
              {booking.subtotal != null && (
                <>
                  <div className="flex justify-between text-neutral-600">
                    <span>Subtotal</span><span>₹{booking.subtotal.toLocaleString("en-IN")}</span>
                  </div>
                  <div className="flex justify-between text-neutral-600">
                    <span>Taxes & fees (12%)</span><span>₹{(booking.taxes || 0).toLocaleString("en-IN")}</span>
                  </div>
                </>
              )}
            </div>
            <div className="flex justify-between mb-6">
              <span className="font-serif text-lg">Total</span>
              <span className="font-serif text-2xl text-midnight" data-testid="checkout-total">
                ₹{booking.total_amount.toLocaleString("en-IN")}
              </span>
            </div>
            {order?.provider !== "manual_upi" && (
              <>
                <Button
                  onClick={() => pay(true)}
                  disabled={processing || booking.status === "confirmed"}
                  className="w-full bg-midnight hover:bg-midnight-hover text-bone py-6"
                  data-testid="pay-btn"
                >
                  {booking.status === "confirmed" ? (
                    <span className="flex items-center gap-2"><CheckCircle size={18} weight="fill" /> Paid</span>
                  ) : processing ? (
                    "Processing..."
                  ) : (
                    <span className="flex items-center gap-2"><DeviceMobile size={16} weight="fill" /> Pay ₹{booking.total_amount.toLocaleString("en-IN")}</span>
                  )}
                </Button>
                {order && (
                  <p className="text-[11px] text-center text-emerald-700 mt-3">
                    🔒 UPI, Cards, Netbanking &amp; Wallets · Razorpay
                  </p>
                )}
              </>
            )}
            {order?.provider === "manual_upi" && (
              <p className="text-[11px] text-center text-emerald-700 mt-3">
                💚 Direct UPI · Verified within 2-4 hrs
              </p>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
